import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function SetupWizard() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const apiUrl = import.meta.env.VITE_API_URL || 'https://sign.virtue.dk';

  const handleSkip = async () => {
    // Mark setup as completed/skipped
    try {
      await fetch(`${apiUrl}/api/tenants/complete-setup`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (error) {
      console.error('Failed to mark setup complete:', error);
    }
    navigate('/dashboard');
  };

  const handleGrantConsent = () => {
    window.location.href = `${apiUrl}/api/auth/consent`;
  };

  const handleComplete = async () => {
    await handleSkip(); // Same as skip - just marks as complete
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        background: 'white',
        borderRadius: '16px',
        padding: '60px',
        maxWidth: '700px',
        width: '100%',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
      }}>
        {/* Header */}
        <div style={{ marginBottom: '40px', textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>🖊️</div>
          <h1 style={{ margin: '0 0 10px', fontSize: '32px', color: '#1f2937' }}>
            Welcome to VirtueSign
          </h1>
          <p style={{ color: '#6b7280', fontSize: '16px', margin: 0 }}>
            Let's get your email signatures set up in a few simple steps
          </p>
        </div>

        {/* Progress Indicator */}
        <div style={{ marginBottom: '40px' }}>
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  width: '40px',
                  height: '6px',
                  borderRadius: '3px',
                  background: i <= step ? '#667eea' : '#e5e7eb',
                  transition: 'background 0.3s',
                }}
              />
            ))}
          </div>
        </div>

        {/* Step Content */}
        {step === 1 && (
          <Step1
            onNext={handleGrantConsent}
            onSkip={handleSkip}
          />
        )}

        {step === 2 && (
          <Step2
            onNext={() => setStep(3)}
            onSkip={handleSkip}
          />
        )}

        {step === 3 && (
          <Step3
            onComplete={handleComplete}
            onSkip={handleSkip}
          />
        )}
      </div>
    </div>
  );
}

function Step1({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) {
  const [showEmailOption, setShowEmailOption] = useState(false);
  const [adminEmail, setAdminEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState('');
  const apiUrl = import.meta.env.VITE_API_URL || 'https://sign.virtue.dk';

  const handleSendEmail = async () => {
    if (!adminEmail || !adminEmail.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setSendingEmail(true);
    setError('');
    
    try {
      const response = await fetch(`${apiUrl}/api/auth/send-consent-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ adminEmail })
      });

      if (response.ok) {
        setEmailSent(true);
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to send email. Please try again.');
      }
    } catch (err) {
      setError('Failed to send email. Please try again.');
    } finally {
      setSendingEmail(false);
    }
  };

  if (emailSent) {
    return (
      <div>
        <div style={{ textAlign: 'center', marginBottom: '30px' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>✓</div>
          <h2 style={{ fontSize: '24px', marginBottom: '15px', color: '#1f2937' }}>
            Email Sent!
          </h2>
          <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
            We've sent the admin consent link to <strong>{adminEmail}</strong>.<br />
            They can click the link to grant permissions.
          </p>
        </div>
        <button
          onClick={onSkip}
          style={{
            width: '100%',
            background: '#667eea',
            color: 'white',
            padding: '14px 28px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          Continue to Dashboard
        </button>
      </div>
    );
  }

  if (showEmailOption) {
    return (
      <div>
        <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#1f2937' }}>
          Send Consent Link to Admin
        </h2>
        <p style={{ color: '#6b7280', lineHeight: '1.6', marginBottom: '30px' }}>
          Enter the email address of a Global Administrator in your organization.
          We'll send them a link to grant admin consent.
        </p>
        
        <div style={{ marginBottom: '30px' }}>
          <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#1f2937' }}>
            Admin Email Address
          </label>
          <input
            type="email"
            value={adminEmail}
            onChange={(e) => {
              setAdminEmail(e.target.value);
              setError('');
            }}
            placeholder="admin@company.com"
            style={{
              width: '100%',
              padding: '12px',
              border: error ? '1px solid #ef4444' : '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '16px',
            }}
          />
          {error && (
            <p style={{ color: '#ef4444', fontSize: '14px', marginTop: '8px' }}>{error}</p>
          )}
        </div>

        <div style={{ display: 'flex', gap: '15px' }}>
          <button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            style={{
              flex: 1,
              background: sendingEmail ? '#9ca3af' : '#667eea',
              color: 'white',
              padding: '14px 28px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: sendingEmail ? 'not-allowed' : 'pointer',
            }}
          >
            {sendingEmail ? 'Sending...' : 'Send Email'}
          </button>
          <button
            onClick={() => setShowEmailOption(false)}
            style={{
              padding: '14px 28px',
              background: 'transparent',
              color: '#6b7280',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
            }}
          >
            Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#1f2937' }}>
        Connect to Microsoft 365
      </h2>
      <p style={{ color: '#6b7280', lineHeight: '1.6', marginBottom: '30px' }}>
        VirtueSign needs permission to access your Microsoft 365 organization to:
      </p>
      <ul style={{ color: '#6b7280', lineHeight: '1.8', marginBottom: '30px', paddingLeft: '20px' }}>
        <li>Read user profile information (name, email, title, phone)</li>
        <li>Read group memberships for template assignments</li>
        <li>Sync organizational data for signature generation</li>
      </ul>
      <p style={{ color: '#6b7280', lineHeight: '1.6', marginBottom: '40px', fontSize: '14px' }}>
        <strong>Note:</strong> This requires admin consent from your Microsoft 365 administrator.
        No data is stored permanently - we fetch it in real-time when needed.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        <button
          onClick={onNext}
          style={{
            width: '100%',
            background: '#667eea',
            color: 'white',
            padding: '14px 28px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          I'm a Global Admin - Grant Consent Now
        </button>
        <button
          onClick={() => setShowEmailOption(true)}
          style={{
            width: '100%',
            background: 'white',
            color: '#667eea',
            padding: '14px 28px',
            border: '2px solid #667eea',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          Send Link to Global Admin
        </button>
        <button
          onClick={onSkip}
          style={{
            width: '100%',
            padding: '14px 28px',
            background: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
        >
          Skip for Now
        </button>
      </div>
    </div>
  );
}

function Step2({ onNext, onSkip }: { onNext: () => void; onSkip: () => void }) {
  return (
    <div>
      <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#1f2937' }}>
        Sync Your Organization
      </h2>
      <p style={{ color: '#6b7280', lineHeight: '1.6', marginBottom: '30px' }}>
        Now that you've granted consent, we can sync your users and groups from Microsoft 365.
        This usually takes just a few seconds.
      </p>
      
      <div style={{
        background: '#f9fafb',
        padding: '30px',
        borderRadius: '8px',
        marginBottom: '30px',
        textAlign: 'center',
      }}>
        <div style={{ fontSize: '48px', marginBottom: '15px' }}>👥</div>
        <p style={{ color: '#6b7280', margin: 0 }}>
          Users and groups will be synced automatically.<br />
          You can manually sync later from the dashboard.
        </p>
      </div>

      <div style={{ display: 'flex', gap: '15px' }}>
        <button
          onClick={onNext}
          style={{
            flex: 1,
            background: '#667eea',
            color: 'white',
            padding: '14px 28px',
            border: 'none',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: 'pointer',
          }}
        >
          Continue
        </button>
        <button
          onClick={onSkip}
          style={{
            padding: '14px 28px',
            background: 'transparent',
            color: '#6b7280',
            border: '1px solid #d1d5db',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '500',
            cursor: 'pointer',
          }}
        >
          Skip
        </button>
      </div>
    </div>
  );
}

function Step3({ onComplete, onSkip }: { onComplete: () => void; onSkip: () => void }) {
  return (
    <div>
      <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#1f2937' }}>
        You're All Set! 🎉
      </h2>
      <p style={{ color: '#6b7280', lineHeight: '1.6', marginBottom: '30px' }}>
        Your VirtueSign account is ready to use. Here's what you can do next:
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', marginBottom: '30px' }}>
        <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '24px' }}>📝</span>
          <div>
            <strong style={{ color: '#1f2937' }}>Create Templates</strong>
            <p style={{ color: '#6b7280', margin: '5px 0 0', fontSize: '14px' }}>
              Design professional email signatures with our visual builder
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '24px' }}>👥</span>
          <div>
            <strong style={{ color: '#1f2937' }}>Assign Templates</strong>
            <p style={{ color: '#6b7280', margin: '5px 0 0', fontSize: '14px' }}>
              Assign signatures to groups or individual users
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '24px' }}>📧</span>
          <div>
            <strong style={{ color: '#1f2937' }}>Install Add-in</strong>
            <p style={{ color: '#6b7280', margin: '5px 0 0', fontSize: '14px' }}>
              Deploy the Outlook add-in to auto-insert signatures
            </p>
          </div>
        </div>
      </div>

      <button
        onClick={onComplete}
        style={{
          width: '100%',
          background: '#667eea',
          color: 'white',
          padding: '14px 28px',
          border: 'none',
          borderRadius: '8px',
          fontSize: '16px',
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        Go to Dashboard
      </button>
    </div>
  );
}
